<template>
    <div class="olarkjs"></div>
</template>

<script setup lang="ts">
const { locale, locales } = useI18n();

useHead({
    script: { src: '/olark.js' },
});

onMounted(() => {
    window.olarkInformation = {
        lang: 'en',
        localisation: '/uk/scripts/olarklocalisation',
        delayMessage:
            'Sorry, but due to overwhelming demand for our live chat service, I may take a few seconds to answer your question. I will be with you as soon as possible.',
    };

    InitLiveChatScripts();

    document
        .querySelectorAll('.livechat, .openOlark a, .modalLiveChat>a')
        .forEach((el) => el.addEventListener('click', (e) => window.olark('api.box.expand')));
});

function InitLiveChatScripts() {
    if (typeof window.olarkInformation === 'undefined') {
        return;
    } else {
        InitLiveChat(
            window.olarkInformation?.lang,
            window.olarkInformation?.showLocalisedImage,
            window.olarkInformation?.delayMessage
        );
    }
}

function InitLiveChat(lang, showLocalisedImage, delayMessage) {
    const filteredLocale: string = locales.value.filter((e) => e.code === locale.value)?.[0]?.storyblok;
    if (filteredLocale !== 'en') {
        if (filteredLocale === 'fr') {
            window.olark.configure('system.group', '56155643dc5e247f54be3ac12700eaaa'); /*Routes to FR Specialist*/
        } else if (filteredLocale === 'it') {
            window.olark.configure('system.group', 'ef65f0c772c1c76182ec7b7861f66ede'); /*Routes to IT Specialist*/
        } else if (filteredLocale === 'de') {
            window.olark.configure('system.group', 'e454aa0c4dc47203ce445cbdd0215150'); /*Routes to DE Specialist*/
        } else if (filteredLocale === 'es') {
            window.olark.configure('system.group', '91f7bc04b651e839b50bc9270fb55219'); /*Routes to ES Group*/
        } else if (filteredLocale === 'pt') {
            window.olark.configure('system.group', 'eb0a909cd3e758d6e66e760cddc3d6e2'); /*Routes to PT/New Group*/
        } else {
            window.olark.configure('system.group', '974f477d893c72efe87b4cc53dd7fbd6'); /*Routes to EU Specialist*/
        }
        if (showLocalisedImage === 'True') {
            window.olark.configure(
                'CalloutBubble.bubble_image_url',
                'https://www.travelparkinggroup.com/media/images/olark/online-' + lang.ToUpper() + '.png'
            );
            window.olark.configure(
                'CalloutBubble.offline_bubble_image_url',
                'https://www.travelparkinggroup.com/media/images/olark/offline-' + lang.ToUpper() + '.png'
            );
        }
    } else {
        window.olark.configure('system.group', '2a97e9ec732847f25e42b24f3e394003'); /*Routes to EN Specialist*/
    }

    window.olark.configure('features.prechat_survey', true);

    // Modification options
    window.olark.configure('system.ask_for_name', 'required');
    window.olark.configure('system.ask_for_email', 'optional');
    window.olark.configure('system.ask_for_phone', 'hidden');

    const secondsUntilAutoDelay = 120;
    let didSendMessageToVisitor = false;

    window.olark('api.chat.onMessageToVisitor', function () {
        didSendMessageToVisitor = true;
    });

    window.olark('api.rules.defineRule', {
        id: 'delayed_response',
        description: "Will send a response to the visitor after an operator doesn't respond.",

        condition: function (pass) {
            window.olark('api.visitor.getDetails', function (details) {
                if (
                    details.isConversing &&
                    !didSendMessageToVisitor &&
                    details.secondsSinceLastMessageToOperator >= secondsUntilAutoDelay
                ) {
                    pass();
                }
            });
        },

        action: function () {
            window.olark('api.chat.sendMessageToVisitor', { body: delayMessage });
        },

        perVisitor: true,
    });
    showLiveChatBox();
}

function showLiveChatBox() {
    const culture = document.documentElement.lang;
    //Phil/Dan asked to add live chat back in for en and it - wrike id: 253682352
    if (culture == 'en-GB' || culture == 'it-IT') window.olark('api.box.show');
    else window.olark('api.box.hide');
}
</script>

<style lang="postcss">
#habla_window_div,
#habla_window_div h1,
#habla_window_div h2,
#habla_window_div h3,
#habla_window_div h4,
#habla_window_div h5,
#habla_window_div h6,
#habla_window_div p,
#habla_window_div td,
#habla_window_div dl,
#habla_window_div tr,
#habla_window_div dt,
#habla_window_div ol,
#habla_window_div form,
#habla_window_div select,
#habla_window_div option,
#habla_window_div pre,
#habla_window_div div,
#habla_window_div table,
#habla_window_div th,
#habla_window_div tbody,
#habla_window_div tfoot,
#habla_window_div caption,
#habla_window_div thead,
#habla_window_div ul,
#habla_window_div li,
#habla_window_div address,
#habla_window_div blockquote,
#habla_window_div dd,
#habla_window_div fieldset,
#habla_window_div li,
#habla_window_div iframe,
#habla_window_div strong,
#habla_window_div legend,
#habla_window_div em,
#habla_window_div s,
#habla_window_div cite,
#habla_window_div span,
#habla_window_div input,
#habla_window_div sup,
#habla_window_div label,
#habla_window_div dfn,
#habla_window_div object,
#habla_window_div big,
#habla_window_div q,
#habla_window_div font,
#habla_window_div samp,
#habla_window_div acronym,
#habla_window_div small,
#habla_window_div img,
#habla_window_div strike,
#habla_window_div code,
#habla_window_div sub,
#habla_window_div ins,
#habla_window_div textarea,
#habla_window_div var,
#habla_window_div a,
#habla_window_div abbr,
#habla_window_div applet,
#habla_window_div del,
#habla_window_div kbd,
#habla_window_div tt,
#habla_window_div b,
#habla_window_div i,
#habla_window_div hr,
#habla_window_div article,
#habla_window_div aside,
#habla_window_div dialog,
#habla_window_div figure,
#habla_window_div footer,
#habla_window_div header,
#habla_window_div hgroup,
#habla_window_div menu,
#habla_window_div nav,
#habla_window_div section,
#habla_window_div time,
#habla_window_div mark,
#habla_window_div audio,
#habla_window_div video {
    background-attachment: scroll;
    background-color: transparent;
    background-image: none;
    background-position: 0 0;
    background-repeat: repeat;
    border-color: #000;
    border-style: none;
    border-width: medium;
    clear: none;
    clip: auto;
    color: inherit;
    counter-increment: none;
    counter-reset: none;
    cursor: auto;
    direction: inherit;
    display: inline;
    float: none;
    font-family: inherit;
    font-size: inherit;
    font-style: inherit;
    font-variant: normal;
    font-weight: inherit;
    height: auto;
    letter-spacing: normal;
    line-height: inherit;
    list-style-type: inherit;
    list-style-position: inside;
    list-style-image: none;
    margin: 0;
    max-height: none;
    max-width: none;
    outline: invert none medium;
    overflow: visible;
    padding: 0;
    position: static;
    quotes: '' '';
    table-layout: auto;
    text-align: inherit;
    text-decoration: inherit;
    text-transform: none;
    unicode-bidi: normal;
    vertical-align: baseline;
    visibility: visible;
    white-space: normal;
    width: auto;
    word-spacing: normal;
    z-index: auto;
    border-radius: 0;
    -moz-border-radius: 0;
    -webkit-border-radius: 0;
    opacity: 1;
}

#habla_window_div,
#habla_window_div th,
#habla_window_div table,
#habla_window_div h3,
#habla_window_div h5,
#habla_window_div p,
#habla_window_div h1,
#habla_window_div td,
#habla_window_div dl,
#habla_window_div tr,
#habla_window_div dt,
#habla_window_div h6,
#habla_window_div ol,
#habla_window_div form,
#habla_window_div select,
#habla_window_div option,
#habla_window_div pre,
#habla_window_div div,
#habla_window_div tbody,
#habla_window_div h2,
#habla_window_div tfoot,
#habla_window_div caption,
#habla_window_div thead,
#habla_window_div h4,
#habla_window_div ul,
#habla_window_div address,
#habla_window_div blockquote,
#habla_window_div dd,
#habla_window_div fieldset,
#habla_window_div textarea,
#habla_window_div hr,
#habla_window_div article,
#habla_window_div aside,
#habla_window_div dialog,
#habla_window_div figure,
#habla_window_div footer,
#habla_window_div header,
#habla_window_div hgroup,
#habla_window_div menu,
#habla_window_div nav,
#habla_window_div section {
    display: block;
}

#habla_window_div nav ul,
#habla_window_div nav ol {
    list-style-type: none;
}

#habla_window_div ul,
#habla_window_div menu {
    list-style-type: disc;
}

#habla_window_div ol {
    list-style-type: decimal;
}

#habla_window_div ol ul,
#habla_window_div ul ul,
#habla_window_div menu ul,
#habla_window_div ol menu,
#habla_window_div ul menu,
#habla_window_div menu menu {
    list-style-type: circle;
}

#habla_window_div ol ol ul,
#habla_window_div ol ul ul,
#habla_window_div ol menu ul,
#habla_window_div ol ol menu,
#habla_window_div ol ul menu,
#habla_window_div ol menu menu,
#habla_window_div ul ol ul,
#habla_window_div ul ul ul,
#habla_window_div ul menu ul,
#habla_window_div ul ol menu,
#habla_window_div ul ul menu,
#habla_window_div ul menu menu,
#habla_window_div menu ol ul,
#habla_window_div menu ul ul,
#habla_window_div menu menu ul,
#habla_window_div menu ol menu,
#habla_window_div menu ul menu,
#habla_window_div menu menu menu {
    list-style-type: square;
}

#habla_window_div li {
    display: list-item;
    min-height: auto;
    min-width: auto;
}

#habla_window_div strong {
    font-weight: 700;
}

#habla_window_div em {
    font-style: italic;
}

#habla_window_div kbd,
#habla_window_div samp,
#habla_window_div code {
    font-family: monospace;
}

#habla_window_div a,
#habla_window_div a *,
#habla_window_div input[type='submit'],
#habla_window_div button,
#habla_window_div input[type='radio'],
#habla_window_div input[type='checkbox'],
#habla_window_div select {
    cursor: pointer;
}

#habla_window_div a:hover {
    text-decoration: underline;
}

#habla_window_div input[type='hidden'] {
    display: none;
}

#habla_window_div abbr[title],
#habla_window_div acronym[title],
#habla_window_div dfn[title] {
    cursor: help;
    border-bottom-width: 1px;
    border-bottom-style: dotted;
}

#habla_window_div ins {
    background-color: #ff9;
    color: #000;
}

#habla_window_div del {
    text-decoration: line-through;
}

#habla_window_div blockquote,
#habla_window_div q {
    quotes: none;
}

#habla_window_div blockquote:before,
#habla_window_div blockquote:after,
#habla_window_div q:before,
#habla_window_div q:after,
#habla_window_div li:before,
#habla_window_div li:after {
    content: '';
    content: none;
}

#habla_window_div input,
#habla_window_div select {
    vertical-align: middle;
}

#habla_window_div select,
#habla_window_div textarea,
#habla_window_div input {
    border: 1px solid #ccc;
}

#habla_window_div table {
    border-collapse: collapse;
    border-spacing: 0;
}

#habla_window_div hr {
    display: block;
    height: 1px;
    border: 0;
    border-top: 1px solid #ccc;
    margin: 1em 0;
}

#habla_window_div [dir='rtl'] {
    direction: rtl;
}

#habla_window_div mark {
    background-color: #ff9;
    color: #000;
    font-style: italic;
    font-weight: 700;
}

#habla_window_div input[type='submit'],
#habla_window_div_base button {
    width: auto;
    font-size: 12px;
    padding: 3px 10px;
    margin: 5px 0 10px;
    border-radius: 3px;
    min-height: 0;
    clear: both;
}

#habla_window_div button:disabled {
    cursor: inherit;
}

#habla_window_div {
    font-size: 100%;
    line-height: 1;
    direction: ltr;
    text-align: left;
    color: #000;
    font-style: normal;
    font-weight: 400;
    text-decoration: none;
}

#habla_window_div input[type='submit'],
#habla_window_div button {
    width: auto;
    font-size: 14px;
}

#habla_window_div [class*='span'] {
    float: none;
    margin-left: 0;
}

#habla_window_div.habla_window_div_base {
    display: block !important;
    z-index: 99999999;
}

#habla_window_div #olark-callout-bubble-offline,
#habla_window_div #olark-callout-bubble-online {
    position: relative !important;
}

#habla_window_div #olark-callout-bubble,
#habla_window_div #olark-callout-bubble-offline {
    position: relative !important;
}

#habla_window_div #habla_panel_div {
    overflow: hidden;
    margin: 0px -12px 0px 59px;
}

#habla_window_div #habla_middle_div {
    padding: 6px 10px 3px;
}

:first-child + html #habla_window_div #habla_middle_div {
    padding: 6px 0 0;
}

#habla_window_div textarea {
    max-width: 100%;
    width: 100%;
    box-sizing: content-box;
    resize: none;
    overflow: hidden;
}

#habla_window_div #habla_offline_body_input,
#habla_window_div #habla_wcsend_input {
    resize: vertical !important;
    overflow: auto !important;
    max-height: 500px !important;
}

:first-child + html #habla_window_div textarea {
    width: 97%;
}

#habla_window_div #habla_input_div {
    margin-left: -17px;
    margin-right: -5px;
}

:first-child + html #habla_window_div #habla_input_div {
    margin-left: 0;
    margin-right: 0;
    width: 95%;
}

#habla_window_div #habla_chatform_form {
    padding: 3px 10px 0;
}

#habla_window_div #habla_conversation_div {
    padding: 6px 10px 0;
    margin: -6px -10px 0;
}

:first-child + html #habla_window_div #habla_conversation_div,
:first-child + html #habla_window_div #habla_pre_chat_div,
:first-child + html #habla_window_div #habla_offline_message_div {
    width: 97%;
}

#habla_window_div #habla_name_input,
#habla_window_div #habla_offline_email_input,
#habla_window_div #habla_offline_body_input,
#habla_window_div #habla_pre_chat_name_input,
#habla_window_div #habla_pre_chat_email_input,
#habla_window_div #habla_offline_phone_input,
#habla_window_div #habla_pre_chat_phone_input {
    overflow: hidden;
    box-sizing: content-box;
}

#habla_window_div #habla_pre_chat_div,
#habla_window_div #habla_offline_message_sent_div,
#habla_window_div #habla_offline_message_div {
    padding: 0 10px;
    margin-left: -10px;
}

:first-child + html #habla_window_div #habla_pre_chat_div,
:first-child + html #habla_window_div #habla_offline_message_sent_div,
:first-child + html #habla_window_div #habla_offline_message_div {
    padding: 5px;
    margin-left: 0;
}

#habla_window_div {
    font-size: 14px;
}

#habla_middle_div {
    line-height: 1.5em;
}

#habla_window_div #habla_expanded_div {
    border-left: 1px solid #666666;
    border-right: 1px solid #666666;
}

#habla_window_div.habla_window_div_position_inline .habla_panel_border {
    border-bottom: 1px solid #ededed;
}

#habla_window_div.olrk-fixed-bottom .habla_panel_border,
#habla_window_div.olrk-fixed-bottom #habla_topbar_div {
    -moz-border-radius-topleft: 5px;
    -moz-border-radius-topright: 5px;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
}

#habla_window_div.olrk-fixed-top .habla_panel_border {
    -moz-border-radius-bottomleft: 5px;
    -moz-border-radius-bottomright: 5px;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
}

#habla_window_div.olrk-fixed-top #habla_expanded_div {
    border-bottom: 1px solid #ededed;
}

#habla_window_div .habla_conversation_div {
    background: transparent none repeat scroll 0 0;
    border-bottom: 1px dotted #ededed;
    line-height: 1.5em;
    overflow: auto;
    color: #000;
    width: 100%;
}

#habla_window_div #habla_wcsend_input {
    background: transparent none repeat scroll 0 0;
    overflow: auto;
    padding: 5px;
    vertical-align: text-top;
    line-height: 1.5em;
}

#habla_window_div .habla_wcsend_input_normal {
    border: 1px solid #b6b6b6;
    color: #000;
}

#habla_window_div .habla_wcsend_input_pre {
    color: gray;
}

#habla_window_div .habla_wcsend_input_highlighted {
    border-color: gray !important;
    color: #000;
}

#habla_window_div .habla_conversation_p_item {
    background: transparent none repeat scroll 0 0;
    color: #000;
    padding: 0;
    margin: 0 0 0 20px;
    text-indent: -20px;
    overflow: visible;
    clear: both;
}

#habla_window_div .habla_conversation_person1 {
    color: #979797;
    padding-right: 5px;
    display: inline;
}

#habla_window_div .habla_conversation_person2 {
    color: #5ba0d0;
    padding-right: 5px;
}

#habla_window_div .habla_conversation_p_item > span:empty {
    padding-right: 0;
}

#habla_window_div .habla_conversation_p_item > span:empty + span {
    display: inline-block;
    text-indent: 0;
}

#habla_window_div .olrk_avatar {
    float: right;
    border: 1px solid lightgray;
    margin-left: 5px;
    margin-bottom: 5px;
}

#habla_window_div #habla_offline_message_span,
#habla_window_div #habla_pre_chat_span {
    margin-bottom: 5px;
    display: block;
}

#habla_window_div #habla_offline_message_div,
#habla_window_div #habla_pre_chat_div {
    line-height: 1.5em;
}

#habla_window_div #habla_offline_message_span {
    margin-bottom: 5px;
    display: block;
}

#habla_window_div #habla_name_input,
#habla_window_div #habla_offline_email_input,
#habla_window_div #habla_offline_body_input,
#habla_window_div #habla_pre_chat_name_input,
#habla_window_div #habla_pre_chat_email_input,
#habla_window_div #habla_pre_chat_phone_input,
#habla_window_div #habla_offline_phone_input,
#habla_window_div #habla_pre_chat_phone_input {
    border: 1px solid #e4e4e4;
    padding: 4px;
    margin-bottom: 5px;
    font-size: 0.9em;
    border-radius: 3px;
    -moz-border-radius: 3px;
}

#habla_window_div .habla_offline_submit_input {
    background: #d54f27 none repeat scroll 0 0;
    border: medium none #ededed;
    color: #fff;
    float: right;
    cursor: pointer;
    border-radius: 5px;
    -moz-border-radius: 5px;
    -webkit-border-radius: 5px;
    padding: 1px 6px;
    margin: 5px -10px 10px 0;
}

#habla_window_div .habla_offline_error_span,
#habla_window_div #habla_pre_chat_error_span {
    float: left;
    font-style: italic;
    line-height: 1.5em;
}

#habla_window_div #habla_topbar_div {
    background: #5ba0d0 none repeat scroll 0 0;
    color: #fff;
    padding: 10px;
    cursor: pointer;
}

#habla_window_div #habla_oplink_a {
    color: #fff;
    text-decoration: none;
}

#habla_window_div #habla_oplink_a.habla_oplink_a_hover {
    text-decoration: underline;
}

#habla_window_div .clear_style {
    clear: both;
}

#habla_window_div .habla_button {
    float: right;
    margin-top: -1px;
    margin-left: 4px;
    padding: 0;
    width: 16px;
    height: 16px;
    border-radius: 5px !important;
    -moz-border-radius: 5px !important;
    -webkit-border-radius: 5px !important;
    background-position: 0 top;
    background-repeat: no-repeat !important;
    cursor: pointer !important;
    overflow: hidden;
    line-height: 1000px;
    background-image: url(../images/sprite.png);
}

#habla_window_div .habla_button:hover {
    background-color: transparent;
}

#habla_window_div #habla_sizebutton_a:hover {
    background-color: transparent;
}

#habla_window_div #habla_closebutton_a {
    background-position: 0 top !important;
}

#habla_window_div #habla_closebutton_a:hover {
    background-color: #333;
}

#habla_window_div #habla_popout_a {
    background-position: -16px top !important;
}

#habla_window_div #habla_popout_a:hover {
    background-color: #333;
}

#habla_window_div .habla_conversation_notification_nickname {
    display: none;
}

#habla_window_div .habla_conversation_notification {
    display: block !important;
    text-indent: 0 !important;
    padding: 5px 8px !important;
    margin: 10px 0 10px -20px !important;
    zoom: 1;
    color: #fff !important;
    -ms-filter: progid:DXImageTransform.Microsoft.gradient(startColorstr=#88000000,endColorstr=#88000000);
    filter: progid:DXImageTransform.Microsoft.gradient(startColorstr=#88000000,endColorstr=#88000000);
    background-color: rgba(0, 0, 0, 0.5) !important;
    -webkit-border-radius: 3px;
    -moz-border-radius: 3px;
    border-radius: 3px;
}

#habla_window_div button:disabled {
    color: #fff;
}

#habla_window_div #habla_panel_div #habla_expanded_div a,
#habla_window_div #habla_panel_div #habla_conversation_div a {
    color: #246092;
}

#habla_window_div #habla_panel_div #habla_expanded_div a:visited,
#habla_window_div #habla_panel_div #habla_conversation_div a:visited {
    color: #246092;
}

#habla_window_div #habla_panel_div #habla_expanded_div a:active,
#habla_window_div #habla_panel_div #habla_conversation_div a:active {
    color: #246092;
}

#habla_conversation_div .olark-feedback-wrapper {
    background-color: #fff !important;
    border: 1px solid #e6e6e6;
    border-radius: 0;
    -webkit-border-radius: 0;
    -moz-border-radius: 0;
    color: #4d4d4d;
    padding: 10px 10px 12px !important;
    filter: none !important;
}

#habla_conversation_div p .olark-feedback-wrapper {
    color: #4d4d4d;
    color: rgba(0, 0, 0, 0.7);
    line-height: 1.2em;
    font-size: 13px;
}

#habla_conversation_div p.olark-feedback-question {
    color: #4d4d4d;
    color: rgba(0, 0, 0, 0.7);
    font-weight: 700;
}

#habla_conversation_div .olark-feedback-error {
    background-color: #ff9581;
    border-radius: 2px;
    -webkit-border-radius: 2px;
    -moz-border-radius: 2px;
    color: #fff;
    margin-bottom: 3px;
    padding: 5px;
}

#habla_conversation_div .olark-feedback-hidden {
    display: none;
}

#habla_conversation_div .olark-feedback-low-answer,
#habla_conversation_div .olark-feedback-high-answer {
    background: #e4e089;
    border-radius: 2px;
    -webkit-border-radius: 2px;
    -moz-border-radius: 2px;
    color: #4d4d4d;
    display: inline-block;
    font-size: 12px;
    line-height: 1em;
    margin: 8px 0 15px;
    padding: 5px;
    position: relative;
    text-align: center;
    max-width: 42%;
}

#habla_conversation_div .olark-feedback-high-answer {
    float: right;
}

#habla_conversation_div .olark-feedback-low-answer:after,
#habla_conversation_div p .olark-feedback-low-answer:before,
#habla_conversation_div .olark-feedback-high-answer:after,
#habla_conversation_div p .olark-feedback-high-answer:before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
}

#habla_conversation_div .olark-feedback-low-answer:after,
#habla_conversation_div p .olark-feedback-low-answer:before {
    left: 5px;
    bottom: -5px;
    border: 4px solid;
    border-color: #e4e089 transparent transparent #e4e089;
}

#habla_conversation_div .olark-feedback-high-answer:after,
#habla_conversation_div p .olark-feedback-high-answer:before {
    right: 5px;
    bottom: -5px;
    border: 4px solid;
    border-color: #e4e089 #e4e089 transparent transparent;
}

#habla_conversation_div .olark-feedback-choices-wrap {
    border-top: 2px solid #cacaca;
    clear: both;
    height: 15px;
    text-align: justify;
}

#habla_conversation_div .olark-feedback-radio {
    -webkit-appearance: radio;
}

#habla_conversation_div .olark-feedback-input {
    border: 0;
    display: inline-block;
    margin-top: -20px;
}

#habla_conversation_div .olark-feedback-choices-wrap:after {
    content: '';
    width: 100%;
    display: inline-block;
}

#habla_conversation_div .olark-feedback-question-number {
    color: #4d4d4d;
    display: inline-block;
    font-size: 13px;
    padding: 6px 0;
    margin-right: 5px;
}

#habla_conversation_div .olark-feedback-text {
    box-sizing: border-box;
    color: #4d4d4d;
    font-size: 14px;
    min-height: 100px;
    margin-bottom: 5px;
    padding: 3px;
}

#habla_conversation_div .olark-feedback-placeholder {
    color: #aaa;
}

#habla_conversation_div .olark-feedback-submit {
    background: #1eaedb;
    border: 0;
    border-radius: 2px;
    -webkit-border-radius: 2px;
    -moz-border-radius: 2px;
    color: #fff;
    display: inline-block;
    font-size: 13px;
    font-weight: 700;
    line-height: 1em;
    padding: 5px;
}

#habla_conversation_div .olark-feedback-submit:disabled {
    background: #ddd;
    color: #4d4d4d;
}

@-webkit-keyframes olark-pulse {
    0% {
    }

    50% {
        background-color: rgba(255, 0, 0, 0.7);
    }

    100% {
    }
}

@-webkit-keyframes olark-tab_in_bottom {
    0% {
        margin-bottom: -50px;
        padding-bottom: 10px;
    }

    50% {
        margin-bottom: 0;
        padding-bottom: 15px;
    }

    100% {
        padding-bottom: 10px;
    }
}

@-webkit-keyframes olark-tab_in_top {
    0% {
        margin-top: -50px;
        padding-top: 10px;
    }

    50% {
        margin-top: 0;
        padding-top: 15px;
    }

    100% {
        padding-top: 10px;
    }
}

.olrk-state-compressed .olrk-fixed-bottom #habla_topbar_div {
    -webkit-animation-name: olark-tab_in_bottom;
    -webkit-animation-duration: 0s;
    -webkit-animation-iteration-count: 1;
    -webkit-animation-direction: alternate;
    -webkit-animation-timing-function: ease-in-out;
}

.olrk-state-compressed .olrk-fixed-top #habla_topbar_div {
    -webkit-animation-name: olark-tab_in_top;
    -webkit-animation-duration: 0s;
    -webkit-animation-iteration-count: 1;
    -webkit-animation-direction: alternate;
    -webkit-animation-timing-function: ease-in-out;
}

#habla_window_div .habla_topbar_div_highlighted {
    background: #d05c34 none repeat scroll 0 0;
    color: #fff;
    -webkit-animation-name: olark-pulse;
    -webkit-animation-duration: 0s;
    -webkit-animation-iteration-count: 2;
    -webkit-animation-direction: alternate;
    -webkit-animation-timing-function: ease-in-out;
}

.olrk-state-compressed .olrk-fixed-bottom #habla_sizebutton_a {
    background-position: -80px top !important;
}

.olrk-state-expanded .olrk-fixed-bottom #habla_sizebutton_a {
    background: url(../images/sprite.png) no-repeat -203px -525px;
}

.olrk-state-expanded .olrk-fixed-bottom #habla_sizebutton_a:hover {
    background: purple url(../images/sprite.png) no-repeat -203px -525px !important;
}

.olrk-state-compressed .olrk-fixed-top #habla_sizebutton_a {
    background-position: -96px top !important;
}

.olrk-state-expanded .olrk-fixed-top #habla_sizebutton_a {
    background-position: -80px top !important;
}

#habla_window_div * {
    box-sizing: content-box;
    -moz-box-sizing: content-box;
    -o-box-sizing: content-box;
}

.hbl_pal_main_bg {
    background-color: #fff !important;
}

.hbl_pal_title_fg {
    color: #000 !important;
}

.hbl_pal_title_bg {
    background-color: #ffc926 !important;
}

.hbl_pal_remote_fg {
    color: purple !important;
}

.hbl_pal_offline_submit_bg {
    background-color: purple !important;
}

div.hbl_pal_main_height {
    height: 250px !important;
}

div.hbl_pal_main_width {
    width: 350px !important;
}

.olrk-fixed-top {
    position: fixed;
    bottom: auto;
    top: 0;
}

.olrk-fixed-bottom {
    position: fixed;
    bottom: 0;
    top: auto;
}

.olrk-fixed-left {
    position: fixed;
    right: auto;
    left: 0;
}

.olrk-fixed-right {
    position: fixed;
    right: 0;
    left: auto;
}

.habla_window_div_position {
    bottom: 0;
    position: fixed;
    right: 0;
    margin-right: 10px;
    margin-bottom: 10px;
}

.habla_window_div_position_floating {
    bottom: 0;
    position: fixed;
    right: 0;
    margin-right: 10px;
    margin-bottom: 10px;
}

.habla_window_div_position_floating_ie {
    bottom: 0;
    position: absolute;
    right: 0;
    margin-right: 10px;
    margin-bottom: 10px;
}

#habla_window_div .habla_conversation_p_item.hbl_pal_main_fg {
    box-sizing: border-box;
    padding: 0 0 22px;
    margin: 0 20px 10px;
    background: transparent;
    position: relative;
    text-indent: 0;
    overflow: hidden;
    -webkit-border-radius: 10px;
    -moz-border-radius: 10px;
    -ms-border-radius: 10px;
    border-radius: 10px;
}

#habla_window_div .habla_conversation_person2.hbl_pal_remote_fg,
#habla_window_div .habla_conversation_person1.hbl_pal_local_fg {
    margin: 0;
    margin-left: 10px;
    background: #eae2ed;
    position: absolute;
    width: 250px;
    height: 100%;
    z-index: 0;
    text-indent: -9999px;
    padding: 2% 20px;
    border: 0;
    -webkit-border-radius: 10px;
    -moz-border-radius: 10px;
    -ms-border-radius: 10px;
    border-radius: 10px;
}

#habla_window_div .habla_conversation_person2.hbl_pal_remote_fg {
    text-indent: 15px;
    background: #f5f5f5;
    padding: 2%;
    margin-left: 0;
    margin-right: 10px;
}

#habla_window_div .habla_conversation_text_span.hbl_pal_main_fg {
    position: relative;
    z-index: 1;
    display: block;
    padding: 23px 30px 0;
}

#habla_conversation_div .olark-feedback-wrapper {
    box-sizing: border-box;
    background-color: #eae2ed !important;
    border: 1px solid purple !important;
    padding: 10px 10px 12px !important;
    filter: none !important;
    width: 270px !important;
    color: purple !important;
    margin-left: 0 !important;
    border: 0;
    -webkit-border-radius: 10px;
    -moz-border-radius: 10px;
    -ms-border-radius: 10px;
    border-radius: 10px;
    text-align: center;
}

.openOlark {
    display: block;
    padding: 0 30px 10px;
    margin: 0;
    text-indent: -30px;
    font-size: 18px;
    line-height: 1.1em;
}

.openOlark a {
    white-space: normal;
}

.openOlark .icon-speech:before {
    position: relative;
    left: 10px;
    top: 15px;
}
</style>
